import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Cascader,
  Checkbox,
  Dropdown,
  Empty,
  message,
  Modal,
  Select,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import type { DefaultOptionType } from 'antd/es/cascader';
import { ArrowDownOutlined, ArrowUpOutlined, CaretDownOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  listBswProjects,
  compareRequirements,
  executeSync,
  getOperationLogs,
  getMainlineProjectsWithReqs,
  type BswProject,
  type BswRequirement,
  type OperationLog,
} from '@/api/requirementSync';
import { useSearchParams } from 'react-router-dom';
import { reviewFlowback, syncUpdate } from '@/api/requirementFlowback';
import { useAuthStore } from '@/store/authStore';
import { getProject } from '@/api/config';
import FieldChangesView from './FieldChangesView';

const { Text, Title } = Typography;

// ── 列宽比例（总计100%，避免横向滚动）──
const COLUMN_WIDTH_RATIOS = {
  select: 2,
  reqId: 8,
  owner: 6,
  lastChangeDatetime: 8,
  description: 16,
  version: 5,
  planStage: 7,
  validationMethod: 6,
  validationCriteria: 16,
  catagory: 6,
  catagory2: 6,
  reqSource: 12,
};

// ── 高亮色（与 BVP 完全一致）──
const HL_DIFF: React.CSSProperties = { backgroundColor: '#DDA0DD', color: '#000', padding: '1px 2px', borderRadius: 2 };
const HL_SRC:  React.CSSProperties = { backgroundColor: '#90EE90', color: '#000', padding: '1px 2px', borderRadius: 2 };
const HL_DES:  React.CSSProperties = { backgroundColor: '#FFFF99', color: '#000', padding: '1px 2px', borderRadius: 2 };

type HighlightType = 'diff' | 'srcOnly' | 'desOnly';

interface TextDiffResult {
  segments: { text: string; type: 'equal' | 'delete' }[];
  hasChanges: boolean;
}

type ReqWithHL = BswRequirement & {
  _highlights?: Record<string, HighlightType>;
  _textDiffs?: Record<string, TextDiffResult>;
};

// ── 字符级文本差异（移植自 BVP computeTextDiff）──
function computeTextDiff(text1: string, text2: string): TextDiffResult {
  if (!text1 && !text2) return { segments: [], hasChanges: false };
  if (!text1) return { segments: [], hasChanges: true };
  if (!text2) return { segments: [{ text: text1, type: 'delete' }], hasChanges: true };
  if (text1 === text2) return { segments: [{ text: text1, type: 'equal' }], hasChanges: false };

  const segments: TextDiffResult['segments'] = [];
  let hasChanges = false;
  let i = 0, j = 0;

  while (i < text1.length || j < text2.length) {
    const equalStart = i;
    while (i < text1.length && j < text2.length && text1[i] === text2[j]) { i++; j++; }
    if (i > equalStart) segments.push({ text: text1.substring(equalStart, i), type: 'equal' });

    if (i < text1.length || j < text2.length) {
      hasChanges = true;
      const maxSearch = Math.min(text1.length - i, text2.length - j, 50);
      let nextI = i, nextJ = j;
      outer:
      for (let sl = 1; sl <= maxSearch; sl++) {
        for (let o1 = 0; o1 <= sl && i + o1 < text1.length; o1++) {
          for (let o2 = 0; o2 <= sl && j + o2 < text2.length; o2++) {
            if (o1 === 0 && o2 === 0) continue;
            let ti = i + o1, tj = j + o2, ml = 0;
            while (ti + ml < text1.length && tj + ml < text2.length &&
                   text1[ti + ml] === text2[tj + ml] && ml < 10) ml++;
            if (ml >= Math.min(3, Math.min(text1.length - ti, text2.length - tj))) {
              nextI = ti; nextJ = tj;
              break outer;
            }
          }
        }
      }
      if (nextI === i && nextJ === j) {
        if (i < text1.length) segments.push({ text: text1.substring(i), type: 'delete' });
        break;
      }
      if (nextI > i) segments.push({ text: text1.substring(i, nextI), type: 'delete' });
      i = nextI; j = nextJ;
    }
  }
  return { segments, hasChanges };
}

// ── 高亮计算（移植自 BVP useRequirementDataComparison）──
function applyHighlights(src: BswRequirement[], des: BswRequirement[]): { src: ReqWithHL[]; des: ReqWithHL[] } {
  const s: ReqWithHL[] = JSON.parse(JSON.stringify(src));
  const d: ReqWithHL[] = JSON.parse(JSON.stringify(des));
  s.forEach(i => { i._highlights = {}; i._textDiffs = {}; });
  d.forEach(i => { i._highlights = {}; i._textDiffs = {}; });

  const sm = new Map(s.map(i => [i.reqId, i]));
  const dm = new Map(d.map(i => [i.reqId, i]));

  const empty = (v: unknown) =>
    v == null || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0);
  const eq = (a: unknown, b: unknown): boolean => {
    if (empty(a) && empty(b)) return true;
    if (empty(a) || empty(b)) return false;
    if (Array.isArray(a) && Array.isArray(b))
      return a.length === b.length && a.every((v, i) => eq(v, (b as unknown[])[i]));
    return String(a).trim() === String(b).trim();
  };
  const SKIP = new Set(['id', 'reqId', '_highlights', '_textDiffs']);

  const allIds = new Set([...sm.keys(), ...dm.keys()]);
  allIds.forEach(reqId => {
    const si = sm.get(reqId);
    const di = dm.get(reqId);
    if (si && di) {
      const fields = Object.keys(si).filter(k => !SKIP.has(k));
      fields.forEach(f => {
        const sv = (si as unknown as Record<string, unknown>)[f];
        const dv = (di as unknown as Record<string, unknown>)[f];
        if (empty(sv) && empty(dv)) return;
        if (empty(sv) && !empty(dv)) { di._highlights![f] = 'diff'; }
        else if (!empty(sv) && empty(dv)) { si._highlights![f] = 'diff'; }
        else if (!eq(sv, dv)) {
          si._highlights![f] = 'diff';
          di._highlights![f] = 'diff';
          // 字符级文本差异（仅字符串字段）
          if (typeof sv === 'string' && typeof dv === 'string') {
            si._textDiffs![f] = computeTextDiff(sv, dv);
            di._textDiffs![f] = computeTextDiff(dv, sv);
          }
        }
      });
    } else if (si) { si._highlights!['reqId'] = 'srcOnly'; }
    else if (di) { di._highlights!['reqId'] = 'desOnly'; }
  });
  return { src: s, des: d };
}

function hlStyle(item: ReqWithHL, field: string): React.CSSProperties | undefined {
  const t = item._highlights?.[field];
  if (t === 'diff') return HL_DIFF;
  if (t === 'srcOnly') return HL_SRC;
  if (t === 'desOnly') return HL_DES;
  return undefined;
}

// ── 单元格渲染（支持字符级差异）──
function renderCell(val: unknown, item: ReqWithHL, field: string): React.ReactNode {
  const text = val == null ? '' : String(val);
  const style = hlStyle(item, field);
  const diff = item._textDiffs?.[field];

  if (diff?.hasChanges) {
    // 字符级高亮：仅对差异片段应用紫色
    return (
      <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
        {diff.segments.map((seg, i) =>
          seg.type === 'equal'
            ? <span key={i}>{seg.text}</span>
            : <span key={i} style={HL_DIFF}>{seg.text}</span>
        )}
      </span>
    );
  }
  if (style) return <span style={{ ...style, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</span>;
  return <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</span>;
}

// ── 子类别（与 BVP BasicParam.tsx 完全一致）──
const SUBCATEGORY_OPTIONS = [
  { label: 'Mcu及中间件',   value: 'Mcu' },
  { label: '通讯',          value: 'Communication' },
  { label: '传感器',        value: 'Sensor' },
  { label: 'SOC及中间件',   value: 'SocAndMiddleware' },
  { label: '诊断和故障',    value: 'DiagAndFault' },
  { label: '时间同步',      value: 'TimeSync' },
  { label: '升级和上下电',  value: 'UpgradeAndPower' },
  { label: '功能安全',      value: 'FunctionalSafety' },
  { label: '信息安全',      value: 'CyberSecurity' },
  { label: '其他',          value: 'Other' },
];

// ── 日志 ──
const ACTION_COLOR: Record<string, string> = {
  create: 'green', update: 'blue', delete: 'red',
  sync_add: 'cyan', sync_delete: 'orange', sync_replace: 'geekblue',
  status_update: 'purple', export: 'gold', import: 'lime',
};
const ACTION_LABEL: Record<string, string> = {
  create: '创建', update: '更新', delete: '删除',
  sync_add: '同步添加', sync_delete: '同步删除', sync_replace: '同步修改',
  status_update: '状态变更', export: '导出', import: '导入',
};

// ── 列过滤下拉（与 BVP FilterHeader 一致）──
interface FilterHeaderProps {
  title: string;
  field: string;
  data: ReqWithHL[];
  filters: Record<string, string>;
  onFilter: (field: string, val: string) => void;
}
const FilterHeader: React.FC<FilterHeaderProps> = ({ title, field, data, filters, onFilter }) => {
  const active = !!filters[field];
  const uniqueVals = useMemo(
    () => [...new Set(data.map(r => String((r as unknown as Record<string, unknown>)[field] ?? '')).filter(Boolean))],
    [data, field],
  );
  const menuItems = [
    { key: '', label: 'All' },
    ...uniqueVals.map(v => ({ key: v, label: v })),
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <span>{title}</span>
      <Dropdown
        menu={{
          selectedKeys: [filters[field] ?? ''],
          items: menuItems,
          onClick: ({ key }) => onFilter(field, key),
        }}
        trigger={['click']}
      >
        <CaretDownOutlined
          style={{ fontSize: 12, color: active ? '#f5222d' : '#aaa', cursor: 'pointer', marginLeft: 2 }}
        />
      </Dropdown>
    </div>
  );
};

// ── 项目层级联动选择器工具函数 ──
function buildCascaderOptions(projects: BswProject[]) {
  const mainlineProjects = projects.filter(p => p.isMainline);
  const normalProjects = projects.filter(p => !p.isMainline);
  const mountainMap = new Map<string, BswProject[]>();
  normalProjects.forEach(p => {
    const key = p.mountain ?? '未分配';
    if (!mountainMap.has(key)) mountainMap.set(key, []);
    mountainMap.get(key)!.push(p);
  });
  return [
    {
      label: '主线',
      value: '__mainline__',
      children: mainlineProjects.map(p => ({ label: p.project, value: p.project })),
    },
    {
      label: '项目',
      value: '__projects__',
      children: [...mountainMap.entries()].map(([mountain, projs]) => ({
        label: mountain,
        value: `__mtn__${mountain}`,
        children: projs.map(p => ({ label: p.project, value: p.project })),
      })),
    },
  ];
}

function projectToCascaderValue(projectName: string, projects: BswProject[]): string[] | undefined {
  if (!projectName) return undefined;
  const p = projects.find(proj => proj.project === projectName);
  if (!p) return undefined;
  if (p.isMainline) return ['__mainline__', projectName];
  return ['__projects__', `__mtn__${p.mountain ?? '未分配'}`, projectName];
}

function cascaderToProject(values?: (string | number)[]): string {
  if (!values?.length) return '';
  return String(values[values.length - 1]);
}

const cascaderFilter = (input: string, path: DefaultOptionType[]) =>
  path.some(opt => String(opt.label).toLowerCase().includes(input.toLowerCase()));

// ── 主组件 ──
interface FormParams { srcProject: string; srcSubcatagory: string; desProject: string; desSubcatagory: string; }

const RequirementSync: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [projects, setProjects]           = useState<BswProject[]>([]);
  const [projectsLoading, setPL]          = useState(false);
  const [params, setParams]               = useState<FormParams>(() => ({
      srcProject: searchParams.get('srcProject') ?? '',
      srcSubcatagory: searchParams.get('srcSubcatagory') ?? '',
      desProject: searchParams.get('desProject') ?? '',
      desSubcatagory: searchParams.get('desSubcatagory') ?? '',
    }));
  const [compareLoading, setCL]           = useState(false);
  const [rawSrc, setRawSrc]               = useState<BswRequirement[]>([]);
  const [rawDes, setRawDes]               = useState<BswRequirement[]>([]);
  const [hasQueried, setHasQueried]       = useState(false);
  const [srcSelected, setSrcSel]         = useState<ReqWithHL[]>([]);
  const [desSelected, setDesSel]         = useState<ReqWithHL[]>([]);
  const [hiddenReqIds, setHidden]        = useState<Set<string>>(new Set());
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [syncing, setSyncing]             = useState(false);
  const [logs, setLogs]                   = useState<OperationLog[]>([]);
  const [logsLoading, setLL]              = useState(false);
  const [expandedLogKeys, setExpanded]    = useState<number[]>([]);
  const [logProject, setLogProject]       = useState<string>('');  // 日志 Tab 独立的项目选择
  const [activeTab, setActiveTab]         = useState('compare');

  // 黄云朵入口（laggingProjectId）：进入时需校验当前用户是否为该项目负责人/需求修改权限用户
  const laggingProjectId = searchParams.get('laggingProjectId');
  // subcategory 云朵入口（新需求同步）：项目空间 ID，用于权限校验
  const newReqProjectId = searchParams.get('newReqProjectId');
  const user = useAuthStore((s) => s.user);
  const [canSync, setCanSync] = useState(false);
  // 通用写入权限：是否为项目 owner 或在 requirement_change_users 中
  const [canWriteSrc, setCanWriteSrc] = useState(true);
  const [canWriteDes, setCanWriteDes] = useState(true);
  // mainLine 级联弹窗
  const [mainlineCascadeVisible, setMainlineCascadeVisible] = useState(false);
  const [mainlineCascadeTargets, setMainlineCascadeTargets] = useState<string[]>([]);
  const [lockedCascadeTargets, setLockedCascadeTargets] = useState<string[]>([]);
  const [cascadeContainsProjects, setCascadeContainsProjects] = useState<string[]>([]);
  const [pendingMainlineSync, setPendingMainlineSync] = useState<{
    targetProject: string; targetSubcatagory: string;
    rows: ReqWithHL[]; clearFn: () => void;
  } | null>(null);

  // container sizes
  const containerRef      = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableWidth, setTW] = useState(1200);
  const [topPct, setTopPct] = useState(50);
  const dragging = useRef(false);

  // measure table width
  useEffect(() => {
    const update = () => {
      if (tableContainerRef.current) {
        const w = tableContainerRef.current.offsetWidth;
        if (w > 0) setTW(w);
      }
    };
    update();
    const ro = new ResizeObserver(update);
    if (tableContainerRef.current) ro.observe(tableContainerRef.current);
    window.addEventListener('resize', update);
    return () => { ro.disconnect(); window.removeEventListener('resize', update); };
  }, []);

  // drag divider
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return;
      const r = containerRef.current.getBoundingClientRect();
      setTopPct(Math.max(15, Math.min(85, ((e.clientY - r.top) / r.height) * 100)));
    };
    const onUp = () => { dragging.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, []);

  const cascaderOptions = useMemo(() => buildCascaderOptions(projects), [projects]);

  useEffect(() => {
    setPL(true);
    listBswProjects().then(r => { if (r.code === 200) setProjects(r.data); })
      .catch(() => message.error('获取项目列表失败')).finally(() => setPL(false));
  }, []);

  // 云朵入口权限：镜像后端 check_project_editor —— 内部用户(admin)拥有所有权限（方便功能测试），
  // 否则需为项目负责人或需求修改权限用户
  useEffect(() => {
    const cloudPid = laggingProjectId || newReqProjectId;
    if (!cloudPid) { setCanSync(false); return; }
    let cancelled = false;
    getProject(String(cloudPid))
      .then((proj) => {
        if (cancelled) return;
        const ok = !!user && (
          user.user_type === 'internal' ||
          user.username === proj.owner ||
          (proj.requirement_change_users ?? []).some((u) => u.id === user.id)
        );
        setCanSync(ok);
      })
      .catch(() => { if (!cancelled) setCanSync(false); });
    return () => { cancelled = true; };
  }, [laggingProjectId, newReqProjectId, user]);

  // 源/目标项目写入权限：owner 或 requirement_change_users
  const checkProjectWritePermission = useCallback((proj: { owner?: string | null; requirement_change_users?: { id: number }[] }) =>
    !!user && (
      user.username === proj.owner ||
      (proj.requirement_change_users ?? []).some((u) => u.id === user.id)
    ), [user]);

  useEffect(() => {
    if (!params.srcProject || !projects.length) { setCanWriteSrc(true); return; }
    const found = projects.find(p => p.project === params.srcProject);
    if (!found) { setCanWriteSrc(false); return; }
    let cancelled = false;
    getProject(String(found.id))
      .then(proj => { if (!cancelled) setCanWriteSrc(checkProjectWritePermission(proj)); })
      .catch(() => { if (!cancelled) setCanWriteSrc(false); });
    return () => { cancelled = true; };
  }, [params.srcProject, projects, user, checkProjectWritePermission]);

  useEffect(() => {
    if (!params.desProject || !projects.length) { setCanWriteDes(true); return; }
    const found = projects.find(p => p.project === params.desProject);
    if (!found) { setCanWriteDes(false); return; }
    let cancelled = false;
    getProject(String(found.id))
      .then(proj => { if (!cancelled) setCanWriteDes(checkProjectWritePermission(proj)); })
      .catch(() => { if (!cancelled) setCanWriteDes(false); });
    return () => { cancelled = true; };
  }, [params.desProject, projects, user, checkProjectWritePermission]);

  // sort by lastChangeDatetime desc (BVP 行为)
  const sortByTime = (data: BswRequirement[]) =>
    [...data].sort((a, b) => {
      const ta = a.lastChangeDatetime ? new Date(a.lastChangeDatetime).getTime() : 0;
      const tb = b.lastChangeDatetime ? new Date(b.lastChangeDatetime).getTime() : 0;
      return tb - ta;
    });

  // highlighted + filtered + sorted data
  const { srcHL, desHL } = useMemo(() => {
    if (!rawSrc.length && !rawDes.length) return { srcHL: [] as ReqWithHL[], desHL: [] as ReqWithHL[] };
    const { src, des } = applyHighlights(rawSrc, rawDes);
    const applyColFilter = (rows: ReqWithHL[]) =>
      rows.filter(r =>
        !hiddenReqIds.has(r.reqId) &&
        Object.entries(columnFilters).every(([field, val]) =>
          !val || String((r as unknown as Record<string, unknown>)[field] ?? '') === val
        )
      );
    let srcFiltered = applyColFilter(src);
    let desFiltered = applyColFilter(des);
    // 从需求回流跳转进入（URL 带 requirementId）：仅显示该需求ID的差异，不显示同类别其他需求
    const fbReqId = searchParams.get('requirementId');
    if (fbReqId) {
      const desId = searchParams.get('mainlineReqId') || fbReqId;
      srcFiltered = srcFiltered.filter(r => r.reqId === fbReqId);
      desFiltered = desFiltered.filter(r => r.reqId === desId);
    }
    // subcategory 云朵入口（newReqProjectId）：仅显示主线有但项目无的"新增"条目（srcOnly）
    if (searchParams.get('newReqProjectId')) {
      srcFiltered = srcFiltered.filter(r => r._highlights?.reqId === 'srcOnly');
      desFiltered = [];
    }
    return { srcHL: srcFiltered, desHL: desFiltered };
  }, [rawSrc, rawDes, hiddenReqIds, columnFilters, searchParams]);

  const onFilter = useCallback((field: string, val: string) => {
    setColumnFilters(p => ({ ...p, [field]: val }));
  }, []);

  const w = (key: keyof typeof COLUMN_WIDTH_RATIOS) =>
    Math.max(40, Math.floor((COLUMN_WIDTH_RATIOS[key] / 100) * tableWidth));

  // 构建列定义（内联，可访问 srcHL/desHL、columnFilters、onFilter）
  const buildCols = useCallback((
    data: ReqWithHL[],
    selected: ReqWithHL[],
    setSelected: React.Dispatch<React.SetStateAction<ReqWithHL[]>>,
  ): ColumnsType<ReqWithHL> => {
    const allSel = data.length > 0 && selected.length === data.length;
    const indet = selected.length > 0 && selected.length < data.length;

    const col = (title: string, field: keyof typeof COLUMN_WIDTH_RATIOS, dataIndex: string) => ({
      title: <FilterHeader title={title} field={dataIndex} data={data} filters={columnFilters} onFilter={onFilter} />,
      dataIndex,
      key: dataIndex,
      width: w(field),
      render: (val: unknown, record: ReqWithHL) => renderCell(val, record, dataIndex),
    });

    return [
      {
        title: (
          <Checkbox indeterminate={indet} checked={allSel}
            onChange={e => setSelected(e.target.checked ? [...data] : [])} />
        ),
        key: 'select', width: w('select'),
        render: (_: unknown, record: ReqWithHL) => (
          <Checkbox
            checked={selected.some(r => r.id === record.id)}
            onChange={() => setSelected(prev =>
              prev.some(r => r.id === record.id)
                ? prev.filter(r => r.id !== record.id)
                : [...prev, record]
            )}
          />
        ),
      },
      col('需求ID', 'reqId', 'reqId'),
      col('负责人', 'owner', 'owner'),
      {
        title: <FilterHeader title="最后修改" field="lastChangeDatetime" data={data} filters={columnFilters} onFilter={onFilter} />,
        dataIndex: 'lastChangeDatetime', key: 'lastChangeDatetime', width: w('lastChangeDatetime'),
        render: (v: string, record: ReqWithHL) => {
          const text = v ? new Date(v).toLocaleDateString('zh-CN') : '-';
          const style = hlStyle(record, 'lastChangeDatetime');
          return style ? <span style={style}>{text}</span> : text;
        },
      },
      col('描述', 'description', 'description'),
      col('版本', 'version', 'version'),
      col('交付阶段', 'planStage', 'planStage'),
      col('验证方法', 'validationMethod', 'validationMethod'),
      col('验证准则', 'validationCriteria', 'validationCriteria'),
      col('分类', 'catagory', 'catagory'),
      col('二级分类', 'catagory2', 'catagory2'),
      col('需求来源/备注', 'reqSource', 'reqSource'),
    ];
  }, [tableWidth, columnFilters, onFilter]); // eslint-disable-line

  const srcCols = useMemo(() => buildCols(srcHL, srcSelected, setSrcSel), [buildCols, srcHL, srcSelected]);
  const desCols = useMemo(() => buildCols(desHL, desSelected, setDesSel), [buildCols, desHL, desSelected]);

  const fetchLogs = useCallback((overrideProject?: string) => {
    const projName = overrideProject ?? logProject;
    if (!projName) return;
    const proj = projects.find(p => p.project === projName);
    if (!proj) return;
    setLL(true);
    getOperationLogs({ projectId: proj.id })
      .then(r => { if (r.code === 200) setLogs(r.data); })
      .catch(() => message.error('获取日志失败')).finally(() => setLL(false));
  }, [projects, logProject]);

  const handleCompare = useCallback(async () => {
    const { srcProject, srcSubcatagory, desProject, desSubcatagory } = params;
    if (!srcProject || !srcSubcatagory || !desProject || !desSubcatagory) { message.error('请完善所有查询参数'); return; }
    if (srcSubcatagory !== desSubcatagory) { message.warning('源类别与目标类别不一致'); return; }
    setCL(true); setSrcSel([]); setDesSel([]); setHidden(new Set()); setColumnFilters({});
    try {
      const res = await compareRequirements(params);
      if (res.code === 200) {
        setRawSrc(sortByTime(res.data[0]?.data ?? []));
        setRawDes(sortByTime(res.data[1]?.data ?? []));
        setHasQueried(true);
      } else { message.error(res.message || '对比失败'); }
    } catch { message.error('对比请求失败'); }
    finally { setCL(false); }
  }, [params]);

  // 从需求回流页跳转进入（URL 带 srcProject）时，自动触发一次"显示差异"
  const autoCompared = useRef(false);
  useEffect(() => {
    if (autoCompared.current) return;
    if (!searchParams.get('srcProject')) return;
    const { srcProject, srcSubcatagory, desProject, desSubcatagory } = params;
    if (!srcProject || !srcSubcatagory || !desProject || !desSubcatagory) return;
    autoCompared.current = true;
    void handleCompare();
  }, [params, handleCompare, searchParams]);

  // 从需求回流页跳转进入：对比完成后自动选中源侧该需求，便于直接同步/忽略
  const autoSelected = useRef(false);
  useEffect(() => {
    if (autoSelected.current) return;
    const fbReqId = searchParams.get('requirementId');
    if (!fbReqId) return;
    if (!srcHL.length) return;
    const match = srcHL.find(r => r.reqId === fbReqId);
    if (match) {
      autoSelected.current = true;
      setSrcSel([match]);
    }
  }, [srcHL, searchParams]);

  const doSync = async (
    targetProject: string, targetSubcatagory: string,
    rows: ReqWithHL[], onSuccess: () => void,
  ) => {
    if (!rows.length) { message.warning('请先选择需求'); return; }
    setSyncing(true);
    try {
      const r = await executeSync({ type: 'add', project: targetProject, subcatagory: targetSubcatagory, id: rows.map(r => r.id) });
      if (r.code === 200) {
        message.success(`同步成功: ${r.message}`);
        onSuccess();
        await handleCompare();
        if (logProject) fetchLogs();
        // 从需求回流页跳转进入、且同步方向为 项目→主线 时，回调推进回流记录状态为已合入
        const fbId = searchParams.get('flowbackId');
        if (fbId && targetProject === 'mainLine') {
          try {
            await reviewFlowback(Number(fbId), 'sync_to_target');
            message.success('回流记录已标记为已合入');
          } catch (error: unknown) {
            const err = error as { response?: { data?: { detail?: string } } };
            message.error(err?.response?.data?.detail || '回流记录状态更新失败，可到回流页手动处理');
          }
        }
        // 黄云朵入口（laggingProjectId）：executeSync 已重指针完成 mainLine→项目 同步，
        // 仅标记 lagging 记录为已同步使云朵消失（不重复 bcr 同步）
        if (laggingProjectId && targetProject !== 'mainLine') {
          try {
            await syncUpdate(Number(laggingProjectId), {
              requirement_id: searchParams.get('requirementId') || '',
              subcategory: params.desSubcatagory,
              action: 'mark_synced',
            });
          } catch (error: unknown) {
            const err = error as { response?: { data?: { detail?: string } } };
            message.error(err?.response?.data?.detail || '标记已同步失败');
          }
        }
        // 新增需求入口：记录已同步状态，使云朵消失（detect_new 排除已处理）
        if (searchParams.get('newReqProjectId') && targetProject !== 'mainLine') {
          try {
            await Promise.all(rows.map(r => syncUpdate(Number(searchParams.get('newReqProjectId')), {
              requirement_id: r.reqId, subcategory: targetSubcatagory, action: 'mark_synced',
            })));
          } catch (error: unknown) {
            const err = error as { response?: { data?: { detail?: string } } };
            message.error(err?.response?.data?.detail || '标记已同步失败');
          }
        }
      }
      else message.error(`同步失败: ${r.message}`);
    } catch { message.error('同步请求失败'); }
    finally { setSyncing(false); }
  };

  const handleDelete = async () => {
    if (!srcSelected.length && !desSelected.length) { message.warning('请先选择需求'); return; }
    const toDeleteSrc = canWriteSrc ? srcSelected : [];
    const toDeleteDes = canWriteDes ? desSelected : [];
    if (!toDeleteSrc.length && !toDeleteDes.length) {
      message.warning('当前用户对所选项目无写入权限，无法删除');
      return;
    }
    const fmt = (rows: ReqWithHL[]) => rows.map(r => r.reqId).slice(0, 10).join(', ') + (rows.length > 10 ? '...' : '');
    const srcCascadeNeeded = srcIsMainLine && toDeleteSrc.length > 0;
    const desCascadeNeeded = desIsMainLine && toDeleteDes.length > 0;

    // 查询哪些主线项目实际含有待删除的需求
    let srcCascadeProjects: string[] = [];
    let desCascadeProjects: string[] = [];
    if (srcCascadeNeeded && params.srcSubcatagory) {
      try {
        const res = await getMainlineProjectsWithReqs({ subcatagory: params.srcSubcatagory, req_ids: toDeleteSrc.map(r => r.reqId).join(',') });
        if (res.code === 200) srcCascadeProjects = res.data;
      } catch { /* 查询失败则不列出，仍级联删除所有主线项目 */ }
    }
    if (desCascadeNeeded && params.desSubcatagory) {
      try {
        const res = await getMainlineProjectsWithReqs({ subcatagory: params.desSubcatagory, req_ids: toDeleteDes.map(r => r.reqId).join(',') });
        if (res.code === 200) desCascadeProjects = res.data;
      } catch { /* 查询失败则不列出 */ }
    }

    let msg = '确定要删除以下需求吗？此操作不可恢复！';
    if (toDeleteSrc.length) msg += `\n\n源项目（${toDeleteSrc.length}个）：${fmt(toDeleteSrc)}`;
    if (toDeleteDes.length) msg += `\n\n目标项目（${toDeleteDes.length}个）：${fmt(toDeleteDes)}`;
    if (srcCascadeProjects.length > 0) {
      msg += `\n\n⚠️ 以下主线项目包含该需求，将同步删除：\n${srcCascadeProjects.join('、')}`;
    }
    if (desCascadeProjects.length > 0 && desCascadeProjects.join() !== srcCascadeProjects.join()) {
      msg += `\n\n⚠️ 以下主线项目包含该需求，将同步删除：\n${desCascadeProjects.join('、')}`;
    }
    Modal.confirm({
      title: '确认删除', content: <pre style={{ whiteSpace: 'pre-wrap' }}>{msg}</pre>, okType: 'danger',
      onOk: async () => {
        setSyncing(true);
        try {
          if (toDeleteSrc.length) await executeSync({ type: 'delete', project: params.srcProject, subcatagory: params.srcSubcatagory, id: toDeleteSrc.map(r => r.id) });
          if (toDeleteDes.length) await executeSync({ type: 'delete', project: params.desProject, subcatagory: params.desSubcatagory, id: toDeleteDes.map(r => r.id) });
          if (srcCascadeNeeded) {
            const targets = srcCascadeProjects.length > 0 ? srcCascadeProjects : otherMainlineProjects.map(p => p.project);
            for (const proj of targets) {
              await executeSync({ type: 'delete', project: proj, subcatagory: params.srcSubcatagory, id: toDeleteSrc.map(r => r.id) });
            }
          }
          if (desCascadeNeeded) {
            const targets = desCascadeProjects.length > 0 ? desCascadeProjects : otherMainlineProjects.map(p => p.project);
            for (const proj of targets) {
              await executeSync({ type: 'delete', project: proj, subcatagory: params.desSubcatagory, id: toDeleteDes.map(r => r.id) });
            }
          }
          message.success('删除成功'); setSrcSel([]); setDesSel([]);
          await handleCompare(); if (logProject) fetchLogs();
        } catch { message.error('删除请求失败'); }
        finally { setSyncing(false); }
      },
    });
  };

  const handleIgnore = async () => {
    // 从需求回流页跳转进入：忽略该回流需求，置为已忽略（不要求两侧同时选中）
    const fbId = searchParams.get('flowbackId');
    if (fbId) {
      try {
        await reviewFlowback(Number(fbId), 'ignore');
        message.success('回流记录已标记为已忽略');
        const ids = new Set([...srcSelected.map(r => r.reqId), ...desSelected.map(r => r.reqId)]);
        setHidden(prev => { const n = new Set(prev); ids.forEach(id => n.add(id)); return n; });
        setSrcSel([]); setDesSel([]);
      } catch (error: unknown) {
        const err = error as { response?: { data?: { detail?: string } } };
        message.error(err?.response?.data?.detail || '回流记录状态更新失败，可到回流页手动处理');
      }
      return;
    }
    // 黄云朵入口：标记 lagging 记录为已忽略（不要求两侧同时选中）
    if (laggingProjectId) {
      try {
        await syncUpdate(Number(laggingProjectId), {
          requirement_id: searchParams.get('requirementId') || '',
          subcategory: params.desSubcatagory,
          action: 'ignore',
        });
        message.success('已标记为已忽略');
        const ids = new Set([...srcSelected.map(r => r.reqId), ...desSelected.map(r => r.reqId)]);
        if (!ids.size && searchParams.get('requirementId')) ids.add(searchParams.get('requirementId')!);
        setHidden(prev => { const n = new Set(prev); ids.forEach(id => n.add(id)); return n; });
        setSrcSel([]); setDesSel([]);
      } catch (error: unknown) {
        const err = error as { response?: { data?: { detail?: string } } };
        message.error(err?.response?.data?.detail || '标记已忽略失败');
      }
      return;
    }
    // 新增需求入口（newReqProjectId）：主线有项目无的新增，选中源侧调后端记录忽略，前端隐藏
    if (searchParams.get('newReqProjectId')) {
      const ids = srcSelected.map(r => r.reqId);
      if (!ids.length) { message.warning('请先选择要忽略的新增需求'); return; }
      setSyncing(true);
      try {
        await Promise.all(ids.map(id => syncUpdate(Number(searchParams.get('newReqProjectId')), {
          requirement_id: id, subcategory: params.desSubcatagory, action: 'ignore',
        })));
        setHidden(prev => { const n = new Set(prev); ids.forEach(id => n.add(id)); return n; });
        setSrcSel([]); setDesSel([]);
        message.success(`已忽略 ${ids.length} 个新增项`);
      } catch (error: unknown) {
        const err = error as { response?: { data?: { detail?: string } } };
        message.error(err?.response?.data?.detail || '标记忽略失败');
      } finally { setSyncing(false); }
      return;
    }
    // 通用需求同步：上下两侧各选中相同的需求项后隐藏
    const sIds = new Set(srcSelected.map(r => r.reqId));
    const dIds = new Set(desSelected.map(r => r.reqId));
    const common = [...sIds].filter(id => dIds.has(id));
    if (!common.length) { message.warning('请在上下两侧各选中相同的需求项再忽略'); return; }
    setHidden(prev => { const n = new Set(prev); common.forEach(id => n.add(id)); return n; });
    setSrcSel([]); setDesSel([]);
    message.success(`已隐藏 ${common.length} 个差异项`);
  };

  const openMainlineCascadeModal = async (
    targetProject: string, targetSubcatagory: string,
    rows: ReqWithHL[], clearFn: () => void,
  ) => {
    setPendingMainlineSync({ targetProject, targetSubcatagory, rows, clearFn });
    // mainLine 始终锁定勾选（目标本身是 mainLine 时不再列出）
    const locked = targetProject !== 'mainLine' ? ['mainLine'] : [];
    setLockedCascadeTargets(locked);
    setCascadeContainsProjects([]);
    setMainlineCascadeTargets([]);
    setMainlineCascadeVisible(true);
    // 查询哪些非目标、非 mainLine 的主线项目已含有该需求（用于自动预选）
    const otherNonLocked = projects.filter(
      p => p.isMainline && p.project !== targetProject && p.project !== 'mainLine'
    ).map(p => p.project);
    if (otherNonLocked.length > 0 && targetSubcatagory) {
      try {
        const reqIds = rows.map(r => r.reqId).join(',');
        const res = await getMainlineProjectsWithReqs({ subcatagory: targetSubcatagory, req_ids: reqIds });
        if (res.code === 200) {
          const preChecked = res.data.filter(p => otherNonLocked.includes(p));
          setCascadeContainsProjects(preChecked);
          setMainlineCascadeTargets(preChecked);
        }
      } catch { /* 查询失败则不预选 */ }
    }
  };

  const handleMainlineCascadeConfirm = async () => {
    if (!pendingMainlineSync) return;
    setMainlineCascadeVisible(false);
    const { targetProject, targetSubcatagory, rows, clearFn } = pendingMainlineSync;
    setPendingMainlineSync(null);
    setSyncing(true);
    try {
      const r = await executeSync({ type: 'add', project: targetProject, subcatagory: targetSubcatagory, id: rows.map(row => row.id) });
      if (r.code !== 200) { message.error(`同步到 mainLine 失败: ${r.message}`); return; }

      const failed: string[] = [];
      // 合并：锁定项目（已有该需求，强制更新）+ 用户自选项目（新增）
      const allCascadeTargets = [...new Set([...lockedCascadeTargets, ...mainlineCascadeTargets])];
      for (const proj of allCascadeTargets) {
        try {
          await executeSync({ type: 'add', project: proj, subcatagory: targetSubcatagory, id: rows.map(row => row.id) });
        } catch { failed.push(proj); }
      }

      clearFn();
      const totalCascaded = allCascadeTargets.length;
      if (failed.length) message.warning(`mainLine 同步成功，以下主线项目同步失败：${failed.join(', ')}`);
      else message.success(`同步成功${totalCascaded > 0 ? `，已级联同步到 ${totalCascaded} 个主线项目` : ''}`);

      await handleCompare();
      if (logProject) fetchLogs();

      const fbId = searchParams.get('flowbackId');
      if (fbId && targetProject === 'mainLine') {
        try { await reviewFlowback(Number(fbId), 'sync_to_target'); message.success('回流记录已标记为已合入'); }
        catch (error: unknown) { const err = error as { response?: { data?: { detail?: string } } }; message.error(err?.response?.data?.detail || '回流记录状态更新失败'); }
      }
      if (laggingProjectId && targetProject !== 'mainLine') {
        try { await syncUpdate(Number(laggingProjectId), { requirement_id: searchParams.get('requirementId') || '', subcategory: targetSubcatagory, action: 'mark_synced' }); }
        catch (error: unknown) { const err = error as { response?: { data?: { detail?: string } } }; message.error(err?.response?.data?.detail || '标记已同步失败'); }
      }
    } catch { message.error('同步请求失败'); }
    finally { setSyncing(false); }
  };

  const hasBoth = srcSelected.length > 0 && desSelected.length > 0;
  const hasAny  = srcSelected.length > 0 || desSelected.length > 0;
  const cloudMode = !!laggingProjectId;
  const syncDisabled = cloudMode && !canSync;
  // mainLine 主线项目相关
  const otherMainlineProjects = useMemo(
    () => projects.filter(p => p.isMainline && p.project !== 'mainLine'),
    [projects]
  );
  const srcIsMainLine = params.srcProject === 'mainLine';
  const desIsMainLine = params.desProject === 'mainLine';
  // 同步目标/源是否为任意主线项目（用于触发级联弹窗）
  const srcIsAnyMainline = !!projects.find(p => p.project === params.srcProject)?.isMainline;
  const desIsAnyMainline = !!projects.find(p => p.project === params.desProject)?.isMainline;

  const renderCompare = () => (
    <div ref={containerRef} style={{ height: 'calc(100vh - 155px)', display: 'flex', flexDirection: 'column' }}>
      {/* ── 上方：同步源 ── */}
      <div style={{ height: `${topPct}%`, background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,.1)', padding: 8, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexShrink: 0 }}>
          <Text strong style={{ whiteSpace: 'nowrap' }}>同步源</Text>
          <Cascader
            options={cascaderOptions}
            style={{ width: 220 }}
            placeholder="选择源项目"
            loading={projectsLoading}
            value={projectToCascaderValue(params.srcProject, projects)}
            onChange={v => setParams(p => ({ ...p, srcProject: cascaderToProject(v as string[]) }))}
            showSearch={{ filter: cascaderFilter }}
            allowClear
            expandTrigger="click"
            displayRender={labels => labels[labels.length - 1]}
          />
          <Select allowClear style={{ width: 160 }} placeholder="选择类别"
            value={params.srcSubcatagory || undefined}
            onChange={v => setParams(p => ({ ...p, srcSubcatagory: v ?? '', desSubcatagory: v ?? '' }))}
            options={SUBCATEGORY_OPTIONS} />
        </div>
        <div ref={tableContainerRef} style={{ flex: 1, overflow: 'auto' }}>
          <Spin spinning={compareLoading}>
            <Table<ReqWithHL>
              dataSource={srcHL} columns={srcCols} rowKey="id" size="small"
              tableLayout="fixed" scroll={{ x: tableWidth }} pagination={false}
              locale={{ emptyText: hasQueried ? '无差异项' : '请先点击"显示差异"' }}
            />
          </Spin>
        </div>
      </div>

      {/* ── 可拖动分隔条 ── */}
      <div
        style={{ height: 6, background: '#d9d9d9', cursor: 'row-resize', flexShrink: 0, transition: 'background .15s' }}
        onMouseDown={() => { dragging.current = true; }}
        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#f5222d'; }}
        onMouseLeave={e => { if (!dragging.current) (e.currentTarget as HTMLDivElement).style.background = '#d9d9d9'; }}
      />

      {/* ── 下方：同步目标 ── */}
      <div style={{ flex: 1, background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,.1)', padding: 8, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text strong style={{ whiteSpace: 'nowrap' }}>同步目标</Text>
            <Cascader
              options={cascaderOptions}
              style={{ width: 220 }}
              placeholder="选择目标项目"
              loading={projectsLoading}
              value={projectToCascaderValue(params.desProject, projects)}
              onChange={v => setParams(p => ({ ...p, desProject: cascaderToProject(v as string[]) }))}
              showSearch={{ filter: cascaderFilter }}
              allowClear
              expandTrigger="click"
              displayRender={labels => labels[labels.length - 1]}
            />
            <Select allowClear style={{ width: 160 }} placeholder="选择类别"
              value={params.desSubcatagory || undefined}
              onChange={v => setParams(p => ({ ...p, desSubcatagory: v ?? '' }))} options={SUBCATEGORY_OPTIONS} />
            <Button type="primary" onClick={handleCompare} loading={compareLoading}>显示差异</Button>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <Button danger size="small" icon={<DeleteOutlined />} disabled={!hasAny || cloudMode} onClick={handleDelete}>删除选中</Button>
            <Button type="primary" size="small" icon={<ArrowDownOutlined />} loading={syncing}
              disabled={syncing || hasBoth || !srcSelected.length || syncDisabled || !canWriteDes}
              onClick={() => {
                if (desIsAnyMainline) {
                  void openMainlineCascadeModal(params.desProject, params.desSubcatagory, srcSelected, () => setSrcSel([]));
                } else {
                  doSync(params.desProject, params.desSubcatagory, srcSelected, () => setSrcSel([]));
                }
              }}>
              同步↓到目标
            </Button>
            <Button type="primary" size="small" icon={<ArrowUpOutlined />} loading={syncing}
              disabled={syncing || hasBoth || !desSelected.length || cloudMode || !canWriteSrc}
              onClick={() => {
                if (srcIsAnyMainline) {
                  void openMainlineCascadeModal(params.srcProject, params.srcSubcatagory, desSelected, () => setDesSel([]));
                } else {
                  doSync(params.srcProject, params.srcSubcatagory, desSelected, () => setDesSel([]));
                }
              }}>
              同步↑到源
            </Button>
            <Button size="small" style={{ background: '#fa8c16', borderColor: '#fa8c16', color: '#fff' }}
              disabled={!hasAny || syncDisabled} onClick={handleIgnore}>忽略差异</Button>
            {cloudMode && !canSync && (
              <Text type="warning" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>无该项目需求修改权限</Text>
            )}
            {!cloudMode && !canWriteSrc && params.srcProject && (
              <Text type="warning" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>无源项目写入权限</Text>
            )}
            {!cloudMode && !canWriteDes && params.desProject && (
              <Text type="warning" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>无目标项目写入权限</Text>
            )}
            {hiddenReqIds.size > 0 && (
              <Button size="small" onClick={() => setHidden(new Set())}>恢复 {hiddenReqIds.size} 个隐藏</Button>
            )}
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          <Spin spinning={compareLoading}>
            <Table<ReqWithHL>
              dataSource={desHL} columns={desCols} rowKey="id" size="small"
              tableLayout="fixed" scroll={{ x: tableWidth }} pagination={false}
              locale={{ emptyText: hasQueried ? '无差异项' : '请先点击"显示差异"' }}
            />
          </Spin>
        </div>
      </div>
      <Modal
        title="选择同步的主线项目"
        open={mainlineCascadeVisible}
        onOk={handleMainlineCascadeConfirm}
        okButtonProps={{ loading: syncing }}
        onCancel={() => { setMainlineCascadeVisible(false); setPendingMainlineSync(null); }}
        okText="确认同步" cancelText="取消"
      >
        {/* 区域1：mainLine 锁定勾选（目标非 mainLine 时显示） */}
        {lockedCascadeTargets.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <p style={{ margin: '0 0 6px', fontWeight: 500 }}>必选（默认勾选，不可取消）：</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {lockedCascadeTargets.map(proj => (
                <Checkbox key={proj} checked disabled>{proj}</Checkbox>
              ))}
            </div>
          </div>
        )}
        {/* 区域2：已包含该需求的其他主线项目（默认勾选，可取消） */}
        {(() => {
          const containsProjs = projects.filter(
            p => p.isMainline && p.project !== pendingMainlineSync?.targetProject && p.project !== 'mainLine'
              && cascadeContainsProjects.includes(p.project)
          );
          return containsProjs.length > 0 ? (
            <div style={{ marginBottom: 12 }}>
              <p style={{ margin: '0 0 6px', fontWeight: 500 }}>已包含该需求（默认勾选，可取消）：</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {containsProjs.map(p => (
                  <Checkbox
                    key={p.project} value={p.project}
                    checked={mainlineCascadeTargets.includes(p.project)}
                    onChange={e => setMainlineCascadeTargets(prev =>
                      e.target.checked ? [...prev, p.project] : prev.filter(x => x !== p.project)
                    )}
                  >{p.project}</Checkbox>
                ))}
              </div>
            </div>
          ) : null;
        })()}
        {/* 区域3：未包含该需求的其他主线项目（默认不勾选，可选） */}
        {(() => {
          const notContainsProjs = projects.filter(
            p => p.isMainline && p.project !== pendingMainlineSync?.targetProject && p.project !== 'mainLine'
              && !cascadeContainsProjects.includes(p.project)
          );
          return notContainsProjs.length > 0 ? (
            <div>
              <p style={{ margin: '0 0 6px', fontWeight: 500 }}>未包含该需求（可选同步新增）：</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {notContainsProjs.map(p => (
                  <Checkbox
                    key={p.project} value={p.project}
                    checked={mainlineCascadeTargets.includes(p.project)}
                    onChange={e => setMainlineCascadeTargets(prev =>
                      e.target.checked ? [...prev, p.project] : prev.filter(x => x !== p.project)
                    )}
                  >{p.project}</Checkbox>
                ))}
              </div>
            </div>
          ) : null;
        })()}
        {lockedCascadeTargets.length === 0 && projects.filter(p => p.isMainline && p.project !== pendingMainlineSync?.targetProject).length === 0 && (
          <p style={{ color: '#999' }}>暂无其他主线项目</p>
        )}
      </Modal>
    </div>
  );

  const renderLogs = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* 独立项目选择器，不依赖对比区域的 params */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
        <Text type="secondary">查看项目：</Text>
        <Cascader
          options={cascaderOptions}
          style={{ width: 260 }}
          placeholder="选择要查看日志的项目"
          loading={projectsLoading}
          value={projectToCascaderValue(logProject, projects)}
          onChange={v => {
            const proj = cascaderToProject(v as string[]);
            setLogProject(proj);
            if (proj) fetchLogs(proj);
            else setLogs([]);
          }}
          showSearch={{ filter: cascaderFilter }}
          allowClear
          expandTrigger="click"
          displayRender={labels => labels[labels.length - 1]}
        />
        {logProject && (
          <Button size="small" onClick={() => fetchLogs()}>刷新</Button>
        )}
      </div>
      <Spin spinning={logsLoading}>
        {!logProject
          ? <Empty description="请先选择项目" />
          : logs.length === 0 && !logsLoading
            ? <Empty description="该项目暂无同步日志" />
            : (
              <Table<OperationLog>
                dataSource={logs} rowKey="id" size="small" pagination={{ pageSize: 20 }}
                columns={[
                  { title: '时间', dataIndex: 'created_at', key: 'time', width: 170,
                    render: (v: string) => v ? new Date(v).toLocaleString('zh-CN') : '-' },
                  { title: '操作', dataIndex: 'action', key: 'action', width: 110,
                    render: (v: string) => <Tag color={ACTION_COLOR[v] || 'default'}>{ACTION_LABEL[v] || v}</Tag> },
                  { title: '子类别', dataIndex: 'subcatagory', key: 'sub', width: 100, ellipsis: true },
                  { title: '需求ID', dataIndex: 'req_id', key: 'reqId', width: 130, ellipsis: true },
                  { title: '操作人', dataIndex: 'operator', key: 'op', width: 100 },
                  { title: '描述', dataIndex: 'description', key: 'desc', ellipsis: true },
                ]}
                expandable={{
                  expandedRowKeys: expandedLogKeys,
                  onExpand: (exp, r) => setExpanded(prev => exp ? [...prev, r.id] : prev.filter(k => k !== r.id)),
                  expandedRowRender: r => <FieldChangesView log={r} />,
                  rowExpandable: r => !!(r.before_data || r.after_data),
                }}
              />
            )}
      </Spin>
    </div>
  );

  return (
    <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Title level={4} style={{ margin: '8px 0 0' }}>需求同步</Title>
      <Tabs activeKey={activeTab}
        onChange={k => setActiveTab(k)}
        items={[
          { key: 'compare', label: '需求对比同步', children: renderCompare() },
          { key: 'logs',    label: '版本变更日志', children: renderLogs() },
        ]}
      />
    </div>
  );
};

export default RequirementSync;
